import React, { Suspense, SyntheticEvent, useEffect } from "react";
import { FormGroup, Input, Label } from "reactstrap";

import { LocationService } from "@services/location.service";

import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { LocalizedStoresSearchParams } from "@redux/modules/api/data/stores/storeLocator/storeLocator.types";
import {
    broadcastMessage,
    clearMessages,
} from "@redux/modules/value/messages/messages.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
// TODO use yarn add @loadable/component for this import
import { StorePickerAutoComplete } from "@components/Store/StorePicker/storeSearch/storePickerAutoComplete/storePickerAutoComplete.component";

import { MAX_STORES } from "../settings";

type Props = {
    storesList: Array<StoreDetailsModel>;
    disabled: boolean;
    onSearch: (params: LocalizedStoresSearchParams) => void;
    onChangeAmount: (amount: number) => void;
    onGeoLoading: (state: boolean) => void;
    withStock: boolean;
    disabledGeoLocation?: boolean;
    storeName: string;
};

const StoreSearch = ({
    storesList,
    disabled,
    onSearch,
    onChangeAmount,
    onGeoLoading,
    disabledGeoLocation,
    withStock,
    storeName,
}: Props) => {
    const { t } = useGranite();
    const [searchQuery, setSearchQuery] = React.useState(storeName);
    const [filterEmptyStock, setFilterEmptyStock] = React.useState(false);

    useEffect(() => {
        setSearchQuery(storeName);
    }, [storeName]);

    const searchQueryOnLocation = (withEmptyStock?: boolean) => {
        clearMessages();
        onGeoLoading(true);
        LocationService.getCurrentPosition()
            .then(({ latitude, longitude }) => {
                onSearch({
                    limit: MAX_STORES,
                    latitude,
                    longitude,
                    filterEmptyStock: withEmptyStock ?? filterEmptyStock,
                    includeProductAvailability: withStock,
                });
            })
            .catch(() => {
                broadcastMessage({
                    id: "storepicker-location-error",
                    text: t("general.storePicker.modal.noGeoLocation"),
                    status: "danger",
                    dismissible: false,
                });
            })
            .finally(() => {
                onGeoLoading(false);
            });
    };

    const handleCheckboxChanged = (newState: any) => {
        clearMessages();
        setFilterEmptyStock(newState);
        onChangeAmount(3);

        if (searchQuery || disabledGeoLocation) {
            onSearch({
                limit: MAX_STORES,
                textualLocation: searchQuery,
                filterEmptyStock: newState,
                includeProductAvailability: withStock,
            });
        } else {
            searchQueryOnLocation(newState);
        }
    };

    const handleEnter = (
        event: SyntheticEvent<HTMLInputElement>,
        cb: () => void,
    ) => {
        cb();
        let value = event.currentTarget.value;
        if (value || disabledGeoLocation) {
            clearMessages();
            onChangeAmount(3);
            onSearch({
                limit: MAX_STORES,
                textualLocation: value,
                filterEmptyStock,
                includeProductAvailability: withStock,
            });
        } else {
            searchQueryOnLocation();
        }
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <div className={"d-flex"}>
                <div className={"flex-grow-1 pr-2"}>
                    <StorePickerAutoComplete
                        storesList={storesList}
                        disabled={disabled}
                        onEnter={handleEnter}
                        onChange={setSearchQuery}
                        searchQuery={searchQuery}
                        shouldFocus={true}
                        onSearch={(result: any) => {
                            onChangeAmount(3);
                            if (result.customOption) {
                                onSearch({
                                    limit: MAX_STORES,
                                    filterEmptyStock,
                                    textualLocation: result.label,
                                    includeProductAvailability: withStock,
                                });
                            } else {
                                onSearch({
                                    limit: MAX_STORES,
                                    storeId: result.code,
                                    filterEmptyStock,
                                    includeProductAvailability: withStock,
                                });
                            }
                        }}
                    />
                </div>
                <div className={"flex-shrink-1"}>
                    <Button
                        test="storeSearch-button-showStore"
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                            if (searchQuery || disabledGeoLocation) {
                                onSearch({
                                    limit: MAX_STORES,
                                    textualLocation: searchQuery,
                                    filterEmptyStock,
                                    includeProductAvailability: withStock,
                                });
                                clearMessages();
                            } else {
                                searchQueryOnLocation();
                            }
                        }}
                        fullWidth
                    >
                        <strong>
                            {t("general.storepicker.button.showStore")}
                        </strong>
                    </Button>
                </div>
            </div>
            {withStock && (
                <FormGroup className={"py-3"} check>
                    <Label check>
                        <Input
                            disabled={disabled}
                            type="checkbox"
                            checked={filterEmptyStock}
                            onChange={() =>
                                handleCheckboxChanged(!filterEmptyStock)
                            }
                        />
                        {t("general.storepicker.label.instock")}
                    </Label>
                </FormGroup>
            )}
        </Suspense>
    );
};

export default StoreSearch;
